import { CompletedMiniHealthCheck, MiniHealthCheck } from "shared/request/myHealthyAdvantageApi";
import { PageHeader } from "UIPalette/PageHeader/PageHeader";
import { StructuredText, StructuredTextDocument } from "react-datocms";
import Button from "shared/UI/Buttons/Button";
import { useTranslation } from "react-i18next";
import { RoundedCard } from "shared/UI/RoundedCard/RoundedCard";
import { LearningMaterials } from "UIPalette/LearningMaterials/LearningMaterials";
import { TrackerCard } from "UIPalette/TrackerCard/TrackerCard";
import { FourWeekPlanList } from "UIPalette/FourWeekPlanList/FourWeekPlanList";
import cn from "classnames";
import { Column, Row } from "shared/UI/LayoutComponents/LayoutComponents";

export type MiniHealthCheckResultsProps = {
  miniHealthCheck: MiniHealthCheck;
  completed: CompletedMiniHealthCheck;
  onRetakeTest: VoidFunction;
};

export const MiniHealthCheckResults = ({ miniHealthCheck, completed, onRetakeTest }: MiniHealthCheckResultsProps) => {
  const { t } = useTranslation();
  const result = miniHealthCheck.results.find((r) => r.id === completed.resultId);
  const hasTrackersOr4WeekPlans = miniHealthCheck.trackers.length > 0 || miniHealthCheck.fourWeekPlans.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader title={t("completeMiniHealthCheck.title", { name: miniHealthCheck.name })} />
      <p>{t("completeMiniHealthCheck.retakeAdvice")}</p>
      <RoundedCard>
        {result && <h2>{result.title}</h2>}
        {result && <StructuredText data={result.content as StructuredTextDocument} />}
        <div className="flex flex-row justify-evenly w-full pt-6">
          <div className="w-full md:w-80">
            <Button text={t("completeMiniHealthCheck.retakeTest")} onClick={onRetakeTest} />
          </div>
        </div>
      </RoundedCard>

      <Row>
        <Column>
          {miniHealthCheck.trackers.map((tracker) => {
            return <TrackerCard key={`tracker${tracker.id}`} tracker={tracker} />;
          })}

          {miniHealthCheck.fourWeekPlans.length > 0 && (
            <FourWeekPlanList title={t("completeMiniHealthCheck.fourWeekPlans")} plans={miniHealthCheck.fourWeekPlans} />
          )}
        </Column>

        <Column>
          {miniHealthCheck.categories.length > 0 && (
            <div className={cn("flex flex-col gap-2", { "sm:col-start-2": hasTrackersOr4WeekPlans })}>
              <h2 className={"p-0"}>{t("completeMiniHealthCheck.relatedArticles")}</h2>
              <LearningMaterials
                categoryIds={miniHealthCheck.categories.map((c) => c.id)}
                errorMessage={t("completeMiniHealthCheck.relatedArticlesErrorButton")}
                ariaLabel={t("completeMiniHealthCheck.relatedArticles")}
              />
            </div>
          )}
        </Column>
      </Row>
    </div>
  );
};
